import { ModalConfirmation, notify, useModal } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { useUpdateProductsVat } from '@features/products/hooks/useUpdateProductsVat'
import { Product } from '@features/products/types/product'
import { getVatRatePercent } from '@features/products/utils/getVatRatePercent'
import { VatRateItemValue } from '@features/vatRates/elements/VatRateSelect/types/vatRateItemValue'

import { ModalId } from '../../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../enums/queryKeys'

export interface UpdateProductsVatConfirmationModalProps {
  selectedProducts: Product[]
  selectedVatRate?: VatRateItemValue
  onUpdateProductsVatRateCancel: () => void
  onUpdateProductsVatRateSuccess: () => void
}

export const UpdateVATConfirmationModal = ({
  selectedProducts,
  selectedVatRate,
  onUpdateProductsVatRateCancel,
  onUpdateProductsVatRateSuccess,
}: UpdateProductsVatConfirmationModalProps) => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const { close } = useModal(ModalId.UpdateProductsVatConfirmationModal)

  const { updateProductsVat, isLoading: isProcessing } = useUpdateProductsVat({
    onError: () => {
      const message =
        selectedProducts?.length === 1
          ? t('products.table.actions.update_vat_selected.notification.error', { name: selectedProducts[0].name })
          : t('products.table.actions.update_vat_selected.notification.error_other', {
              count: selectedProducts?.length ?? 0,
            })
      notify({
        id: NotificationKeys.UpdateProductsVatError,
        variant: 'error',
        message,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.Products])
      const message =
        selectedProducts?.length === 1
          ? t('products.table.actions.update_vat_selected.notification.success', { name: selectedProducts[0].name })
          : t('products.table.actions.update_vat_selected.notification.success_other', {
              count: selectedProducts?.length ?? 0,
            })
      notify({
        id: NotificationKeys.UpdateProductsVatSuccess,
        variant: 'success',
        message,
      })
      onUpdateProductsVatRateSuccess()
      close()
    },
  })

  const message =
    selectedProducts?.length === 1
      ? t('products.table.actions.update_vat_selected.confirmation_modal.message', {
          name: selectedProducts[0].name,
        })
      : t('products.table.actions.update_vat_selected.confirmation_modal.message_other', {
          count: selectedProducts.length,
        })

  const handleOkClick = useCallback(() => {
    if (selectedVatRate?.rate === undefined || selectedVatRate?.id === undefined) {
      return
    }
    updateProductsVat({
      productIds: selectedProducts.map((selectedProduct) => selectedProduct.id),
      vatPct: selectedVatRate.rate,
      vatRateId: selectedVatRate.id,
    })
  }, [selectedVatRate?.id, selectedVatRate?.rate, updateProductsVat, selectedProducts])

  const handleCancelClick = useCallback(() => {
    onUpdateProductsVatRateCancel()
    close()
  }, [close, onUpdateProductsVatRateCancel])

  return (
    <ModalConfirmation
      cancelLabel={t('products.table.actions.update_vat_selected.confirmation_modal.cancel')}
      danger
      id={ModalId.UpdateProductsVatConfirmationModal}
      message={message}
      okLabel={t('products.table.actions.update_vat_selected.confirmation_modal.confirm_update')}
      okLoading={isProcessing}
      onOk={handleOkClick}
      onCancel={handleCancelClick}
      size="m"
      title={t('products.table.actions.update_vat_selected.confirmation_modal.title', {
        rate: getVatRatePercent(selectedVatRate?.rate),
      })}
    />
  )
}
